import { z } from 'zod';
import { CalendarDateSchema, PaginationQuerySchema, createPaginatedResponseSchema, writeWarningsFields } from './common.js';
import { EntitySourceSchema } from './client.js';
import { ExpenseVatTreatmentSchema } from '../vat.js';
import { RecipientParticularsValidationSchema } from '../tax-invoice-particulars.js';
import { PaymentFundingSourceSchema } from './document-payment.js';
import { InvoiceSortDirectionSchema } from './invoice.js';
// =============================================================================
// Supplier Invoice
// =============================================================================
export const SupplierInvoiceStatusSchema = z.enum(['DRAFT', 'APPROVED', 'PAID', 'VOIDED']);
/**
 * Statuses in which an expense can be billed to a client as a disbursement.
 * Whether the supplier has been paid and whether the client has been billed are
 * independent: a firm usually pays a disbursement before it bills it, so a PAID
 * expense stays billable until an invoice line references it.
 */
export const BILLABLE_SUPPLIER_INVOICE_STATUSES = ['APPROVED', 'PAID'];
export function isBillableSupplierInvoiceStatus(status) {
    return BILLABLE_SUPPLIER_INVOICE_STATUSES.includes(status);
}
export const SupplierInvoiceCreateModeSchema = z.enum(['DRAFT', 'APPROVED']);
export const SupplierInvoiceSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    supplierId: z.string(),
    supplierInvoiceNumber: z.string().nullable(),
    // The supplier's own "Our reference" and our "Your reference" as printed on
    // their document. Null on every row created before the reference split, and
    // on any document that shows neither.
    supplierOwnReference: z.string().nullable(),
    customerReference: z.string().nullable(),
    date: z.string(),
    dueDate: z.string().nullable(),
    status: SupplierInvoiceStatusSchema,
    source: EntitySourceSchema,
    // Set when a document was read for this expense. Optional so a bundle loaded
    // ahead of the backend deploy can still parse the response.
    sourceExtractionId: z.string().nullable().optional(),
    currency: z.string(),
    // Derived from the line items while the expense is a DRAFT, and frozen from
    // them once it is approved (or voided). Never an independent input - see
    // UpdateSupplierInvoiceInputSchema's comment for why they are absent from
    // that schema. A response always carries the RESOLVED values, so a consumer
    // never sees the 0 a DRAFT row stores.
    subtotalMinorUnits: z.number().int(),
    vatAmountMinorUnits: z.number().int(),
    totalMinorUnits: z.number().int(),
    vatRateBasisPoints: z.number().int(),
    ledgerAccountId: z.string().nullable(),
    ledgerEntrySetId: z.string().nullable(),
    notes: z.string().nullable(),
    clientProjectId: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    // ── Recipient particulars, as the source tax invoice printed them ──────────
    //
    // Who the supplier made the document out to, frozen at create from the linked
    // extraction. FACTS ABOUT THE DOCUMENT: what the supplier asserted, never
    // nudged towards our own record. Not accepted on the wire for the same reason
    // `vatRateBasisPoints` is not - they are transcribed server-side from the
    // stored document, and a client that could set them could assert its own
    // compliance.
    //
    // All nine null is NOT "addressed correctly". Whether a document was read at
    // all is answered by `sourceExtractionId`, not by these: all-null without one
    // means no document (NOT CHECKED), all-null WITH one means the document named
    // nobody, which IS a finding. Whether the values satisfy VAT Act s20(4)(c) is
    // derived, not stored; see `recipientValidation` on the detail response.
    recipientName: z.string().nullable(),
    recipientVatNumber: z.string().nullable(),
    recipientRegistrationNumber: z.string().nullable(),
    recipientAddressLine1: z.string().nullable(),
    recipientAddressLine2: z.string().nullable(),
    recipientAddressCity: z.string().nullable(),
    recipientAddressProvince: z.string().nullable(),
    recipientAddressPostalCode: z.string().nullable(),
    recipientAddressCountry: z.string().nullable(),
    // Present on list responses only: max(0, total - recorded OUT payment
    // allocations). Seeds the record-payment dialog's outstanding balance so a
    // partially-paid APPROVED expense pre-fills its remaining amount, not the total.
    outstandingAmountMinorUnits: z.number().int().optional(),
});
/**
 * `SupplierInvoice` plus the shared `warnings` / `warningDetails` pair a
 * write that reverses a payable journal as a side effect can populate (void,
 * reallocate) - the acquisition-reversal conduct warning the ledger returns
 * from `reverseLedgerEntrySet` when the reversed journal is an asset's
 * acquisition journal.
 */
export const MutateSupplierInvoiceResponseSchema = SupplierInvoiceSchema.extend(writeWarningsFields);
// ── Line-item value sets ─────────────────────────────────────────────────────
// Declared here, above the create input that references them: these are Zod
// runtime values, so a schema used by `CreateSupplierInvoiceInputSchema` must
// already be initialised when that object literal is evaluated at module load.
// The read shape and the reconciliation helper live further down, with the rest
// of the line-item surface.
export const SupplierInvoiceLineItemKindSchema = z.enum(['ITEM', 'TIP']);
/**
 * A line as supplied by a caller creating an expense. Lines are how an expense
 * is described: the header's subtotal / VAT / total are SUM(net), SUM(vat) and
 * their sum, computed server-side from exactly these rows and never stated on
 * the wire. Each line carries its own net/VAT split, so there is no basis
 * (inclusive/exclusive) to state: that question is answered once, by whichever
 * process resolved these amounts, and does not travel with the stored line.
 *
 * A caller with no itemisation to offer (a hand-entered expense) sends ONE
 * line carrying the whole amount. That is not a degraded shape - it is what a
 * one-line expense is.
 *
 * Capped description, unlike the read shape: this is an input schema, so
 * bounding the text here is correct (bounding a response schema would instead
 * make an over-long stored row unreadable). The array is bounded too,
 * matching the extraction's own transcription cap.
 */
export const SupplierInvoiceLineItemWriteSchema = z.object({
    kind: SupplierInvoiceLineItemKindSchema,
    description: z.string().max(500).nullable(),
    quantity: z.number().nullable(),
    unitAmountMinorUnits: z.number().int().nullable(),
    netAmountMinorUnits: z.number().int('Net amount must be an integer number of minor units'),
    vatAmountMinorUnits: z.number().int('VAT amount must be an integer number of minor units'),
    // The GL account this line posts to. Optional; omit to inherit the
    // header's default account, or set later via
    // PATCH .../line-items/:lineItemId/allocate. Ownership is verified at the
    // route.
    ledgerAccountId: z.string().uuid().optional().nullable(),
});
/**
 * Body for `POST .../supplier-invoices/:id/line-items`: hand-author a USER
 * line item on a DRAFT expense. `netAmountMinorUnits` / `vatAmountMinorUnits`
 * are both required (unlike the update body below) - a person typing a new
 * line always knows its amount, so there is no partial-patch case here.
 */
/** Response for `PATCH .../line-items/:lineItemId/allocate`, matching the
 * invoice domain's `DeleteInvoiceResponseSchema` shape (a plain success ack -
 * the caller re-fetches the expense if it wants the updated line). */
export const AllocateSupplierInvoiceLineItemResponseSchema = z.object({
    success: z.boolean(),
});
export const AddSupplierInvoiceLineItemBodySchema = z.object({
    kind: SupplierInvoiceLineItemKindSchema,
    description: z.string().max(500).nullable().optional(),
    quantity: z.number().nullable().optional(),
    unitAmountMinorUnits: z.number().int().nullable().optional(),
    netAmountMinorUnits: z.number().int('Net amount must be an integer number of minor units'),
    vatAmountMinorUnits: z.number().int('VAT amount must be an integer number of minor units'),
    ledgerAccountId: z.string().uuid().optional().nullable(),
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
});
/**
 * Body for `PATCH .../line-items/:lineItemId`: edit a DRAFT expense's line
 * item in place. Every accounting/display field is optional (a true partial
 * PATCH, unlike the add body where every field is required) - omit to leave
 * unchanged. Does not carry `ledgerAccountId`: GL allocation is set only via
 * the dedicated `PATCH .../line-items/:lineItemId/allocate` route, so the
 * two concerns (what the line is worth, which account it posts to) stay on
 * separate endpoints with separate ownership checks.
 */
export const UpdateSupplierInvoiceLineItemBodySchema = z.object({
    description: z.string().max(500).nullable().optional(),
    quantity: z.number().nullable().optional(),
    unitAmountMinorUnits: z.number().int().nullable().optional(),
    netAmountMinorUnits: z.number().int('Net amount must be an integer number of minor units').optional(),
    vatAmountMinorUnits: z.number().int('VAT amount must be an integer number of minor units').optional(),
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
});
export const CreateSupplierInvoiceInputSchema = z.object({
    supplierId: z.string().uuid('Supplier ID must be a valid UUID'),
    supplierInvoiceNumber: z.string().max(200, 'Invoice number must be 200 characters or less').optional().nullable(),
    supplierOwnReference: z.string().max(200, 'Their reference must be 200 characters or less').optional().nullable(),
    customerReference: z.string().max(200, 'Our reference must be 200 characters or less').optional().nullable(),
    date: z.string().min(1, 'Date is required'),
    dueDate: z.string().optional().nullable(),
    currency: z.string().length(3, 'Currency must be a 3-letter ISO code').optional(),
    // subtotalMinorUnits / vatAmountMinorUnits / totalMinorUnits are NOT accepted
    // here, for the same reason they are absent from UpdateSupplierInvoiceInput:
    // they are SUM(net) / SUM(vat) over `lineItems` below and their sum. A wire
    // contract that took both would be stating the same fact twice and leaving
    // the server to pick a winner.
    //
    // vatRateBasisPoints is likewise derived, not accepted - round(SUM(vat) /
    // SUM(net) x 10000), a snapshot of the effective rate that cannot drift from
    // the VAT actually charged. A mixed-rate invoice (some lines exempt) yields a
    // blended effective rate, which is exactly what should be recorded. See
    // createSupplierInvoice in the service.
    ledgerAccountId: z.string().uuid().optional().nullable(),
    notes: z.string().max(2000, 'Notes must be 2000 characters or less').optional().nullable(),
    // Attribution: the client project (matter) to associate this expense with for
    // billing recovery. Optional (null = overhead). The client is derived from
    // the matter server-side; supply only clientProjectId.
    clientProjectId: z.string().uuid().optional().nullable(),
    // Provenance link to the extraction job whose scanned PDF seeded this expense.
    // Write-only audit trail; validated server-side against the caller's account.
    sourceExtractionId: z.string().uuid().optional().nullable().describe('Provenance link to a failed extraction whose scanned document should be re-attached. ' +
        'Pass the sourceExtractionId surfaced in an earlier extraction-failure notification; omit otherwise.'),
    // Provenance link to the email attachment whose PDF was used to create this expense.
    // Write-only; present only when created via the email-inbox flow. Validated
    // server-side against the caller's account via requireResourceOwnership.
    sourceEmailAttachmentId: z.string().uuid().optional().nullable(),
    // Provenance link to the stored email that carried the attachment. Write-only;
    // not persisted on the expense row, but emitted for classification-quality analysis.
    sourceStoredEmailId: z.string().uuid().optional().nullable(),
    // Explicit lifecycle choice for manual web creates. Omitted preserves the
    // legacy WEB_APP default of approving on create. DRAFT lets the web app save
    // an entered expense for later review without misusing the source/provenance
    // field.
    createMode: SupplierInvoiceCreateModeSchema.optional(),
    // How the expense was created. Restricted to the provenances a supplier
    // invoice legitimately has (narrower than the full EntitySource the response
    // carries). WEB_APP creates default to approve-on-create unless createMode
    // is DRAFT. CHAT (agent-initiated from extracted invoice data) and
    // EMAIL_AI_DETECTION default to DRAFT so the user reviews and approves
    // explicitly via the Expenses UI or the approveSupplierInvoice route.
    // Restricting the set keeps a client from parking an expense in DRAFT via an
    // unrelated source value (matters once the route's super-admin gate relaxes
    // to account-member).
    source: z.enum(['WEB_APP', 'CHAT', 'EMAIL_AI_DETECTION']).optional(),
    // The expense itself. REQUIRED, and the only description of what it is
    // worth: the header amounts are computed from these rows. Explicit rather
    // than inferred - this endpoint does not look up the source extraction and
    // resolve lines behind the caller's back. A caller with nothing to itemise
    // sends one line carrying the whole amount.
    lineItems: z.array(SupplierInvoiceLineItemWriteSchema).min(1, 'At least one line item is required').max(40),
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
});
export const ApproveSupplierInvoiceInputSchema = z.strictObject({
    vatTreatment: ExpenseVatTreatmentSchema.optional(),
    /** Server-set precondition, hidden from the chat agent (`mcp.hiddenFields`): the approve is refused as `stale-version` if the expense changed since. */
    expectedUpdatedAt: z.iso.datetime().optional(),
});
export const UpdateSupplierInvoiceInputSchema = z.object({
    supplierInvoiceNumber: z.string().max(200, 'Invoice number must be 200 characters or less').optional().nullable(),
    supplierOwnReference: z.string().max(200, 'Their reference must be 200 characters or less').optional().nullable(),
    customerReference: z.string().max(200, 'Our reference must be 200 characters or less').optional().nullable(),
    date: z.string().optional(),
    dueDate: z.string().optional().nullable(),
    currency: z.string().length(3, 'Currency must be a 3-letter ISO code').optional(),
    // subtotalMinorUnits / vatAmountMinorUnits / totalMinorUnits are NOT
    // accepted here: they are derived from the line-item set
    // (SUM(net) / SUM(vat) / their total), so accepting them on the wire is
    // incoherent - the same reason vatRateBasisPoints is absent. Edit the
    // amounts through the line-item endpoints instead
    // (POST/PATCH/DELETE .../line-items).
    ledgerAccountId: z.string().uuid().optional().nullable(),
    notes: z.string().max(2000, 'Notes must be 2000 characters or less').optional().nullable(),
    // Attribution: supply to update matter association; omit entirely to leave
    // attribution unchanged (partial patch). Supply null to clear (overhead).
    clientProjectId: z.string().uuid().optional().nullable(),
});
export const ReallocateSupplierInvoiceInputSchema = z.object({
    ledgerAccountId: z.string().uuid('Ledger account ID must be a valid UUID'),
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
    // Explicit VAT treatment for the repost. Precedence: explicit caller value
    // here > the TARGET account's defaultVatTreatment (never the source/posted
    // posture). Omit to let the target account's default govern the repost.
    vatTreatment: ExpenseVatTreatmentSchema.optional(),
});
export const SupplierInvoiceSortBySchema = z.enum(['date', 'dueDate', 'supplierName', 'status']);
export const SupplierInvoiceListQuerySchema = z.object({
    status: SupplierInvoiceStatusSchema.optional(),
    search: z.string().max(200).optional(),
    // Filter by client project (matter): returns only expenses attributed to the given matter.
    clientProjectId: z.string().uuid().optional(),
    // Defaults to newest expense date first. The sortable columns are the ones the
    // Expenses table shows; see the route description for what each sorts by.
    sortBy: SupplierInvoiceSortBySchema.optional().prefault('date'),
    sortDirection: InvoiceSortDirectionSchema.optional().prefault('desc'),
}).extend(PaginationQuerySchema.shape);
export const SupplierInvoiceListResponseSchema = createPaginatedResponseSchema(SupplierInvoiceSchema);
// =============================================================================
// Supplier Invoice - Line items (the accounting source of truth)
// =============================================================================
//
// Every expense has at least one line, and the lines are what the accounting
// reads: the payable journal posts one debit leg per RESOLVED GL account
// (a line's own `ledgerAccountId`, or the header's as its default), and the
// header's subtotal/vat/total are derived from the lines while the expense is
// a DRAFT and frozen from them at approval. They are written in the same
// request that creates the expense, not copied later by a listener, and there
// is no stored inclusive/exclusive basis - each line carries its own net and
// VAT.
//
// `kind` deliberately mirrors the extraction-side value set
// (SupplierInvoiceExtractedLineItemKindSchema in supplier-invoice-extraction.ts)
// under a domain-level name: extraction rows are transient model output; these
// are durable expense rows.
//
// The durable row is deliberately NARROWER than the extracted one. The
// extraction also captures a per-line VAT marker and a handwritten flag, and
// keeps capturing them - they stay in the shadow `extractedLineItems` JSON,
// which is immutable once COMPLETED and lives exactly as long as the expense.
// Neither graduates here, because nothing reads them: no journal, VAT, billing
// or display path branches on either, and a set of ZERO_RATED lines under a
// header claiming 15% reconciles just fine, since the reconciliation identity
// never looks at the marker. Storing them durably would bake a per-line VAT
// taxonomy - a Postgres enum, the most expensive thing to walk back - ahead of
// the phase-4 decision about what per-line VAT treatment actually means.
// Extract everything; graduate only what is wired.
export const SupplierInvoiceLineItemSchema = z.object({
    id: z.string(),
    /** Page order from the source document, 0-based. */
    sortOrder: z.number().int(),
    kind: SupplierInvoiceLineItemKindSchema,
    /**
     * Deliberately uncapped HERE: this is a read/response shape and must render
     * whatever is stored. The bound on this text is enforced at ingestion, where
     * model output becomes stored bytes (`MAX_LINE_DESCRIPTION_CHARS` in the
     * extraction domain's `normaliseExtractedLineItems`) - capping a response
     * schema instead would turn an over-long legacy row into an unreadable
     * expense rather than a truncated one.
     */
    description: z.string().nullable(),
    quantity: z.number().nullable(),
    /** AS PRINTED, display only - never an accounting input. */
    unitAmountMinorUnits: z.number().int().nullable(),
    /** Ex-VAT amount, minor units; negative for a discount line. REQUIRED. */
    netAmountMinorUnits: z.number().int(),
    /** VAT amount, minor units; 0 for a zero-rated, exempt, no-VAT, or TIP line. REQUIRED. */
    vatAmountMinorUnits: z.number().int(),
    /**
     * The GL account this line posts to. Null inherits the header's own
     * ledgerAccountId (the DEFAULT expense account). An expense is
     * MULTI-ALLOCATED when two or more distinct RESOLVED (line ?? header)
     * accounts appear across its lines.
     */
    ledgerAccountId: z.string().nullable(),
});
/**
 * Tolerance, in minor units, on every line-sum identity - both the extraction
 * domain's verdict derivation (`normaliseExtractedLineItems`) and the
 * graduation/create-time re-verification below.
 *
 * Exported because those must agree by construction: a verdict derived at
 * one tolerance and re-verified at another would silently stop graduating
 * lines, and the symptom (a routine "did not reconcile" log line) is
 * indistinguishable from a user editing the pre-filled amounts.
 */
export const LINE_SUM_TOLERANCE_MINOR = 1;
/**
 * Pure check that a set of TRANSCRIBED (as-printed) line items reconciles with
 * an expense's header amounts on the given basis:
 *  - EXCLUSIVE: sum(ITEM) == subtotal.
 *  - INCLUSIVE: sum(ITEM) == total, or - when every TIP total is readable -
 *    sum(ITEM) + sum(TIP) == total (the handwritten-gratuity receipt, where the
 *    tip bridges the printed total to the amount actually paid).
 * An unreadable ITEM total fails (the sum is not provable); an unreadable TIP
 * total only removes the with-tip identity.
 *
 * Extraction-domain concern only: this operates on the AS-PRINTED transcribed
 * total (`SupplierInvoiceExtractedLineItem.lineTotalMinorUnits`), which
 * decides which basis a document printed on - used by the extraction's own
 * shadow review verdict (`normaliseExtractedLineItems`) and its bill-create
 * form's pre-submit prediction. The supplier-invoice domain's stored lines no
 * longer carry a basis (see `supplierInvoiceLineAmountsReconcile` below for
 * the storage-side check).
 */
export function supplierInvoiceLineItemsReconcile(lines, basis, header) {
    if (lines.length === 0)
        return false;
    const items = lines.filter((l) => l.kind === 'ITEM');
    const tips = lines.filter((l) => l.kind === 'TIP');
    if (items.some((l) => l.lineTotalMinorUnits === null))
        return false;
    // Non-null assertions: the some() guard above proves every ITEM total is
    // non-null; TypeScript cannot narrow array elements through it.
    const itemSum = items.reduce((sum, l) => sum + l.lineTotalMinorUnits, 0);
    if (basis === 'EXCLUSIVE') {
        return Math.abs(itemSum - header.subtotalMinorUnits) <= LINE_SUM_TOLERANCE_MINOR;
    }
    if (Math.abs(itemSum - header.totalMinorUnits) <= LINE_SUM_TOLERANCE_MINOR)
        return true;
    if (tips.length === 0 || tips.some((l) => l.lineTotalMinorUnits === null))
        return false;
    const tipSum = tips.reduce((sum, l) => sum + l.lineTotalMinorUnits, 0);
    return Math.abs(itemSum + tipSum - header.totalMinorUnits) <= LINE_SUM_TOLERANCE_MINOR;
}
/**
 * Pure check that a set of STORED (net/vat) line items reconciles with an
 * expense's header amounts: SUM(net) == subtotal AND SUM(vat) == vat, both
 * within `LINE_SUM_TOLERANCE_MINOR`. No basis to state - each line already
 * carries its own net/VAT split.
 *
 * A RECONCILIATION check, not a storage check: the supplier-invoice domain
 * never runs it, because lines ARE the amounts there - there is no second
 * figure for them to disagree with. It exists for the two callers that hold a
 * document's own declared totals alongside the lines they read off it and must
 * decide whether the transcription can be trusted: extraction graduation
 * (`resolveGraduatedLineItems`) and the Bill create form's pre-submit
 * prediction. Both fall back to a single line carrying the declared total when
 * it fails.
 */
export function supplierInvoiceLineAmountsReconcile(lines, header) {
    if (lines.length === 0)
        return false;
    const netSum = lines.reduce((sum, l) => sum + l.netAmountMinorUnits, 0);
    const vatSum = lines.reduce((sum, l) => sum + l.vatAmountMinorUnits, 0);
    return (Math.abs(netSum - header.subtotalMinorUnits) <= LINE_SUM_TOLERANCE_MINOR &&
        Math.abs(vatSum - header.vatAmountMinorUnits) <= LINE_SUM_TOLERANCE_MINOR);
}
// =============================================================================
// Supplier Invoice - one supplier's expenses (born-composed read)
// =============================================================================
/**
 * Query for `GET /accounts/:accountId/suppliers/:supplierId/expenses`.
 *
 * Deliberately narrower than `SupplierInvoiceListQuerySchema`: the supplier is
 * the path resource, so there is no `supplierId` filter here, and no `search`
 * (free-text search exists on the account-wide list to FIND a supplier's
 * expenses; once you are scoped to one supplier it has no job).
 */
export const SupplierExpensesQuerySchema = z.object({
    status: SupplierInvoiceStatusSchema.optional(),
}).extend(PaginationQuerySchema.shape);
/**
 * One supplier's expenses, with the supplier's identity as the header.
 *
 * The name sits on the envelope, not on each row: it is a property of the
 * resource being requested, not of each expense. Rows are the standard
 * `SupplierInvoiceSchema` shape, so a caller already handling expenses needs no
 * second row type.
 */
export const SupplierExpensesResponseSchema = createPaginatedResponseSchema(SupplierInvoiceSchema).extend({
    supplier: z.object({ id: z.string(), name: z.string() }),
});
// =============================================================================
// Supplier Invoice - Billing Status
// =============================================================================
/**
 * The billing state of a supplier invoice (expense) relative to client invoices.
 * Derived at query time from the existence of an `InvoiceLineItem.supplierInvoiceId`
 * reference; never stored as a column.
 *
 * UNBILLED: no client invoice line has billed this expense yet.
 * BILLED:   exactly one client invoice line references this expense; the
 *           `invoiceId` and `invoiceNumber` of that invoice are included so the
 *           UI can link directly to the invoice.
 */
export const SupplierInvoiceBillingStateSchema = z.enum(['UNBILLED', 'BILLED']);
export const SupplierInvoiceBillingStatusSchema = z.discriminatedUnion('state', [
    z.object({ state: z.literal('UNBILLED') }),
    z.object({
        state: z.literal('BILLED'),
        /** The client invoice on which this expense is billed. */
        invoiceId: z.string(),
        /** The human-readable invoice number (e.g. "INV-0042"), or null for legacy drafts without an allocated number. */
        invoiceNumber: z.string().nullable(),
    }),
]);
// A single recorded OUT payment allocated to a supplier invoice.
// Included in the detail response so the UI knows which paymentId to pass to
// the void route (POST .../payments/:paymentId/void).
export const SupplierInvoicePaymentSchema = z.object({
    id: z.string().uuid(),
    amountMinorUnits: z.number().int().nonnegative(),
    paidAt: z.iso.datetime(),
    reference: z.string().nullable(),
    createdAt: z.iso.datetime(),
    /**
     * True once the payment has been reconciled to a bank statement line (drained
     * from Payments Clearing). Reconciled payments cannot be voided directly from
     * the expense - the bank line must be unmatched first - so the UI hides the
     * per-payment undo for them.
     */
    reconciled: z.boolean(),
    /**
     * The bank statement line that settled this payment, when it was reconciled
     * (drained from Payments Clearing); null for an unreconciled or document-free
     * mint. The raw link behind `reconciled`, exposed so a caller can resolve
     * which line to unmatch without inferring it through Payments Clearing.
     */
    bankStatementLineId: z.string().uuid().nullable(),
    /**
     * Which source funded this payment's settlement leg (the payable-side twin
     * of `InvoiceRecordedPaymentSchema.fundingSource`). Null for legacy or
     * non-ledgered payments. `SUPPLIER_ON_ACCOUNT` identifies an
     * AP-to-AP application of the supplier's on-account remainder, so the UI
     * renders a distinct "On account applied" row. `LEDGER_ACCOUNT` identifies a
     * terminal GL-funded settlement, so the UI does not show a "Pending"
     * bank-reconciliation badge.
     */
    fundingSource: PaymentFundingSourceSchema.nullable(),
});
// =============================================================================
// Supplier banking snapshot: what the document printed, vs where we pay today
// =============================================================================
/**
 * The banking details a supplier invoice PRINTED, exactly as transcribed from
 * the document.
 *
 * Frozen per-document and stamped server-side from the source extraction. Never
 * accepted on the wire: a client that could assert what a document printed could
 * assert a document that does not exist, and comparing one invoice's details
 * against the next - the only way invoice-redirection fraud is detectable - would
 * be worthless.
 *
 * All-null is a valid and common value: a hand-captured expense, an expense from
 * a bank line, a document with no banking block, and every expense predating this
 * feature. All-null means NOT CAPTURED. It is never agreement, and the verdict
 * below says so in its own vocabulary rather than leaving a reader to guess.
 */
export const SupplierBankSnapshotSchema = z.object({
    bankName: z.string().nullable(),
    bankBranchCode: z.string().nullable(),
    bankAccountNumber: z.string().nullable(),
    bankAccountHolderName: z.string().nullable(),
    /** Free text as printed ("Cheque", "Savings"); banks use their own wording. */
    bankAccountType: z.string().nullable(),
});
/**
 * The recipient block a supplier's tax invoice printed, exactly as it appeared.
 *
 * The write-side counterpart of the `recipient*` columns on `SupplierInvoice`,
 * and the sibling of `SupplierBankSnapshot`: a structured shape the create input
 * carries so the caller can hand over one server-resolved value instead of ten
 * loose fields. Never accepted on the wire - a client that could assert what a
 * document printed about the recipient could assert its own s20(4)(c)
 * compliance.
 */
export const SupplierInvoiceRecipientParticularsSchema = z.object({
    name: z.string().nullable(),
    vatNumber: z.string().nullable(),
    registrationNumber: z.string().nullable(),
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    addressCity: z.string().nullable(),
    addressProvince: z.string().nullable(),
    addressPostalCode: z.string().nullable(),
    addressCountry: z.string().nullable(),
});
/**
 * How a document's printed banking details relate to the accounts stored for
 * that supplier.
 *
 * Five values, not three, because the absences are different questions and
 * collapsing them loses the answer a user needs:
 *
 * - `NOT_ON_DOCUMENT` - nothing was printed, so there is nothing to check. NOT a
 *   pass. Every expense created before this feature has this value.
 * - `NO_STORED_ACCOUNT` - the document printed details and we hold none, so
 *   there is nothing to check against. Offer to record it.
 * - `MATCHES_STORED` - an active stored account has the same account number.
 * - `MATCHES_ARCHIVED` - it matches only an account the firm retired. Neither a
 *   clean match nor a new bank; a human should see it in those words.
 * - `DIFFERS_FROM_STORED` - accounts are on file and none matches. This is the
 *   flag, and the reason the snapshot exists.
 *
 * Identity is the normalised account number ALONE. Bank name, holder name,
 * branch code and account type are transcribed wording that varies between
 * documents from the same supplier, so making any of them load-bearing would
 * raise a fraud alarm over a difference in spelling.
 */
export const SupplierBankSnapshotVerdictSchema = z.enum([
    'NOT_ON_DOCUMENT',
    'NO_STORED_ACCOUNT',
    'MATCHES_STORED',
    'MATCHES_ARCHIVED',
    'DIFFERS_FROM_STORED',
]);
/**
 * The snapshot plus its verdict, derived on every read.
 *
 * Deliberately NOT a stored column. The verdict relates a frozen thing (the
 * snapshot) to a live thing (the supplier's accounts, which a user changes in
 * direct response to this verdict), so a stored flag would still read
 * DIFFERS_FROM_STORED against details that have since been recorded and are now
 * correct - it would begin lying at the exact moment it did its job. Deriving is
 * safe because the frozen half is durable: the snapshot lives on the expense.
 *
 * The snapshot values ride along so the UI can pre-fill the record-this-account
 * form from them. That pre-fill is a UI convenience over an ordinary explicit
 * write - the document itself never reaches a write path.
 */
export const SupplierBankSnapshotComparisonSchema = z.object({
    snapshot: SupplierBankSnapshotSchema,
    verdict: SupplierBankSnapshotVerdictSchema,
    /** Non-null exactly for MATCHES_STORED and MATCHES_ARCHIVED. */
    matchedBankAccountId: z.string().nullable(),
});
/**
 * Full supplier invoice detail response, including derived billing status and
 * recorded payment allocations.
 * Returned by GET /accounts/:accountId/supplier-invoices/:id.
 */
export const SupplierInvoiceDetailResponseSchema = SupplierInvoiceSchema.extend({
    billing: SupplierInvoiceBillingStatusSchema,
    // Recorded OUT payments allocated to this expense. Empty array when no
    // payment has been recorded; populated after recordExpensePayment.
    payments: z.array(SupplierInvoicePaymentSchema),
    /**
     * True when the payable journal is posted AND already settled - matched to
     * a bank-statement-line, or covered by a recorded payment. Informational
     * settlement status only: the matter is locked once an expense is approved
     * whether or not it has settled (see `updateSupplierInvoice`'s docstring in
     * `supplier-invoice.service.ts`), so the backend never reads this flag to
     * gate a matter change. Independent of the billed lock (`billing.state`)
     * and the voided lock (`status`), which are already visible via their own
     * fields.
     */
    attributionClassLocked: z.boolean(),
    /**
     * Line items, in document order: always at least one row (see
     * `SupplierInvoice.lineItems` in the Prisma schema). A row may have been
     * graduated from a source document, hand-authored, or backfilled by the
     * lines-always migration - the row does not record which, deliberately:
     * nothing branches on provenance, and the discriminator that once carried
     * it conflated "do these lines reconcile" with "who authored them".
     */
    lineItems: z.array(SupplierInvoiceLineItemSchema),
    /**
     * Whether the banking details the SOURCE DOCUMENT printed still agree with
     * the bank accounts stored for this supplier. Server-derived on every read,
     * never stored and never accepted on the wire - see the schema below.
     */
    bankSnapshot: SupplierBankSnapshotComparisonSchema,
    /**
     * Whether the source tax invoice's recipient particulars (the `recipient*`
     * fields above) satisfy VAT Act s20(4)(c), compared against the firm's own
     * business entity.
     *
     * DERIVED per read, not stored: the row keeps what the document PRINTED, and
     * the comparison is an application-layer question. Correcting the firm's own
     * VAT number therefore re-scores every expense, rather than leaving a stale
     * verdict frozen against the record as it stood when the document arrived.
     *
     * Null means NOT CHECKED - the expense had no document to read, or the account
     * has no business entity to compare against. Never "clean".
     */
    recipientValidation: RecipientParticularsValidationSchema.nullable(),
});
// =============================================================================
// Supplier Invoice Documents
// =============================================================================
export const SupplierInvoiceDocumentResponseSchema = z.object({
    id: z.string(),
    supplierInvoiceId: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    downloadUrl: z.string(),
});
export const SupplierInvoiceDocumentListResponseSchema = z.array(SupplierInvoiceDocumentResponseSchema);
// =============================================================================
// Supplier Invoice - Billable expenses (client-scoped picker)
// =============================================================================
/**
 * An APPROVED expense attributed to a client that has not yet been billed on
 * any invoice line. Used by the draft-invoice editor to present a one-click
 * billable section for the invoice's client.
 */
export const BillableExpenseSchema = z.object({
    supplierInvoiceId: z.string(),
    supplierId: z.string(),
    supplierName: z.string(),
    supplierInvoiceNumber: z.string().nullable(),
    // The remaining references the document (and the supplier relationship) can
    // carry. Present so the picker and omnibox can display and search the
    // reference a fee-earner actually recognises, which is often not the
    // supplier's own document number. Null on every row created before the
    // reference split, and on any document that printed only the one number.
    supplierOwnReference: z.string().nullable(),
    customerReference: z.string().nullable(),
    customerAccountNumber: z.string().nullable(),
    subtotalMinorUnits: z.number().int(),
    totalMinorUnits: z.number().int(),
    currency: z.string(),
    date: z.string(),
    clientProjectId: z.string().nullable(),
});
export const BillableExpenseListResponseSchema = z.array(BillableExpenseSchema);
// =============================================================================
// Supplier Invoice - Credit Notes
// =============================================================================
/**
 * Record a supplier-issued credit note (or discount) against an APPROVED
 * expense with a posted payable journal. Supply the credit's GROSS total
 * only (totalMinorUnits, a positive integer in minor units) - the net/VAT
 * split is always derived server-side from the ORIGINAL posted payable
 * journal, never from a caller-supplied VAT figure. When the original
 * expense posted no VAT, the credit is posted gross with no VAT leg; when it
 * did, the credit reverses input VAT at the original's proportion (a full
 * credit - totalMinorUnits equal to the expense's own total - reverses the
 * exact posted VAT). totalMinorUnits is capped at the expense's current
 * outstanding balance (its total minus whatever has already been settled by
 * payments, bank lines, or earlier credit notes); exceeding it is rejected
 * with a 400. One verb serves a full credit note, a partial credit note, and
 * a plain discount - they are the same operation. date is 'YYYY-MM-DD'.
 * reference is optional free text (e.g. the supplier's own credit-note
 * number) and appears in the posted journal's description.
 */
export const RecordSupplierCreditNoteBodySchema = z.object({
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
    totalMinorUnits: z.number().int().min(1, 'Credit total must be a positive integer'),
    date: CalendarDateSchema,
    reference: z.string().max(200, 'Reference must be 200 characters or less').optional(),
});
export const RecordSupplierCreditNoteResponseSchema = z.object({
    entrySetId: z.string(),
    supplierInvoice: SupplierInvoiceSchema,
});
export const ReverseSupplierCreditNoteResponseSchema = z.object({
    reversalEntrySetId: z.string(),
    supplierInvoice: SupplierInvoiceSchema,
});
/**
 * The AUTHORITATIVE inputs `recordSupplierCreditNote` uses on execute, exposed
 * as a dumb read so a dry-run preview (the `pnpm ledger expense credit-note`
 * CLI) matches execute exactly instead of approximating it from a different,
 * staler shape. Two failure modes this closes: (1) deriving "outstanding" from
 * recorded payments alone undercounts, since a prior credit note posts no
 * payment row - only the tagged-AP net this reader is built on nets those; (2)
 * deriving VAT from the document's own recorded `vatAmountMinorUnits` can
 * diverge from what was actually posted, e.g. a NO_VAT-treated approval posts
 * no VAT leg at all regardless of the document's figure.
 */
/**
 * Optional query for the credit-note preview: the credit-note total the caller
 * intends to record. Supplied so the server returns the AUTHORITATIVE VAT/net
 * split for that exact amount; omitted, the preview describes a credit for the
 * full outstanding balance.
 */
export const SupplierCreditNotePreviewQuerySchema = z.object({
    totalMinorUnits: z.coerce.number().int().positive().optional(),
});
export const SupplierCreditNotePreviewResponseSchema = z.object({
    /**
     * The expense's current outstanding balance in minor units: total minus
     * whatever has already been settled - by payments, bank lines, AND any
     * prior credit notes (all read from the tagged Accounts Payable net, not
     * from payment rows alone). This is the exact figure a credit note's
     * `totalMinorUnits` is capped against on execute.
     */
    outstandingMinorUnits: z.number().int(),
    currency: z.string(),
    /**
     * The expense's own total, in minor units - fixed regardless of
     * settlement. Needed to reproduce the server's exact-vs-proportional VAT
     * split (`creditTotal === totalMinorUnits` reverses the exact posted VAT;
     * otherwise the proportion is derived against this denominator).
     */
    totalMinorUnits: z.number().int(),
    /**
     * The GL account the expense's payable journal posted to - the account a
     * credit note nets against. Null when the expense has never had a payable
     * journal posted (still DRAFT, or accounting was disabled at approval).
     */
    ledgerAccountId: z.string().nullable(),
    /**
     * The EXACT input VAT posted on the expense's payable journal - 0 for a
     * NO_VAT posting, regardless of the document's own recorded
     * `vatAmountMinorUnits`. A credit note's VAT reversal is always derived
     * from this figure server-side, never from the document's recorded VAT.
     */
    postedVatAmountMinorUnits: z.number().int(),
    /**
     * Whether a credit note can currently be recorded against this expense -
     * APPROVED status with a posted payable journal - mirroring
     * `isExpenseEligibleForRecordedPayment` exactly. `false` for a DRAFT,
     * PAID, VOIDED, or never-ledgered expense; the other fields above are
     * still populated on a best-effort basis so the caller can render why.
     */
    eligible: z.boolean(),
    /**
     * Gross total of every LIVE supplier credit note already recorded against
     * this expense (a reversed credit note leaves this figure). Rendered for
     * context; also the `priorCredited` half of the server's cumulative VAT
     * derivation.
     */
    creditedMinorUnits: z.number().int(),
    /**
     * Input VAT those live prior credit notes have already reversed. The
     * server derives a new credit note's VAT as the delta between the
     * credited-so-far VAT target and this figure, which is what makes a
     * sequence of partial credits unwind VAT Control to exactly zero.
     */
    vatReversedMinorUnits: z.number().int(),
    /**
     * The credit-note total this preview describes: the caller's requested
     * amount, or the full `outstandingMinorUnits` when none was supplied
     * (the same default the CLI applies when `--total` is omitted).
     */
    proposedTotalMinorUnits: z.number().int(),
    /**
     * The AUTHORITATIVE input VAT `proposedTotalMinorUnits` would reverse -
     * exactly what execute posts. Callers must render this rather than
     * re-deriving it: the derivation is cumulative over live prior credit
     * notes, so it cannot be reproduced from `postedVatAmountMinorUnits` and
     * `totalMinorUnits` alone.
     */
    proposedVatMinorUnits: z.number().int(),
    /** `proposedTotalMinorUnits` less `proposedVatMinorUnits` - the net leg. */
    proposedNetMinorUnits: z.number().int(),
});
export const SupplierInvoiceCreateAttemptParamsSchema = z.strictObject({
    accountId: z.string().uuid(), attemptToken: z.string().uuid(),
});
export const SupplierInvoiceCreateAttemptResponseSchema = z.object({
    id: z.string().uuid(), status: SupplierInvoiceStatusSchema, ledgerEntrySetId: z.string().uuid().nullable(),
}).nullable();
/** Outstanding at conversion; original invoice totals remain on the document. */
export const SupplierInvoiceOpeningRetagInputSchema = z.strictObject({
    openAmountMinorUnits: z.number().int().min(1).max(2147483647),
});
export const SuggestedExpenseAccountResponseSchema = z.object({
    suggestion: z
        .object({
        ledgerAccountId: z.string().uuid(),
        code: z.string(),
        name: z.string(),
        occurrences: z.number().int().min(1),
        historySize: z.number().int().min(1),
    })
        .nullable(),
});
/** Why an account is suggested, in the words every surface uses. */
export function suggestedExpenseAccountReason({ occurrences, historySize }, supplierName) {
    return historySize === 1
        ? `Used for the only past expense from ${supplierName} with a chosen account.`
        : `Used for ${occurrences} of ${historySize} past expenses from ${supplierName} with a chosen account.`;
}
